import { useState, useEffect } from "react";
import { Download, X, Share } from "lucide-react";
import { useIsStandalone } from "@/hooks/use-standalone";
import { isNativeApp } from "@/lib/native-setup";
import { Link } from "react-router-dom";


interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const isStandalone = useIsStandalone();

  useEffect(() => {
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    setIsIOS(ios);

    const dismissed = localStorage.getItem("pwa-install-dismissed");
    if (dismissed && Date.now() - Number(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    if (!isStandalone) {
      if (ios) {
        setTimeout(() => setShowBanner(true), 2000);
      }
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setTimeout(() => setShowBanner(true), 2000);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem("pwa-install-dismissed", String(Date.now()));
  };

  if (isNativeApp() || isStandalone || !showBanner) return null;

  return (
    <div className="fixed bottom-20 left-3 right-3 z-[60] animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-card border border-border rounded-xl p-4 shadow-lg shadow-black/30">
        <button
          onClick={handleDismiss}
          aria-label="Dismiss install banner"
          className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {isIOS && !deferredPrompt ? (
          <div className="flex items-start gap-3 pr-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Share className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Install Trades Toolbelt</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Tap <Share className="w-3 h-3 inline -mt-0.5" /> then <strong>"Add to Home Screen"</strong>.{" "}
                <Link to="/install" onClick={handleDismiss} className="text-primary underline">Read installation guide</Link>
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 pr-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Download className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">Install Trades Toolbelt</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Add to home screen for quick access.{" "}
                <Link to="/install" onClick={handleDismiss} className="text-primary underline">Read installation guide</Link>
              </p>
            </div>
            <button
              onClick={handleInstall}
              className="shrink-0 bg-primary text-primary-foreground text-xs font-bold px-4 py-2 rounded-lg"
            >
              Install
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstallPrompt;
